import type { FC, ReactNode } from 'react';
import type React from 'react';
import { mdiLockOutline } from '@mdi/js';
import { Icon as IconMdi } from '@mdi/react';
import { signInWithRedirect } from 'aws-amplify/auth';

import Button from '../Button';

interface SignInButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  customProvider: string;
  email: string;
}

const ARTIFICIAL_STAFF_DOMAIN = 'artificial.io';

const PROVIDERS: Record<string, { label: string; icon: ReactNode }> = {
  azure: {
    label: 'Microsoft',
    icon: <img src="/assets/microsoft-logo.svg" alt="Microsoft logo" className="h-5 w-5" />,
  },
  okta: {
    label: 'Okta',
    icon: <img src="/assets/okta-logo.svg" alt="Okta logo" className="h-5 w-5" />,
  },
  auth0: {
    label: 'Artificial',
    icon: <img src="/assets/artificial-logo.svg" alt="Artificial logo" className="h-5 w-5" />,
  },
  google: {
    label: 'Google',
    icon: <img src="/assets/google-logo.svg" alt="Google logo" className="h-5 w-5" />,
  },
};

// A client's own Auth0 connection (e.g. McGill's) also uses the "auth0" prefix, but only
// artificial.io users are actually signing in as Artificial staff — anyone else should see a
// generic "Auth0" label, not our own branding.
const GENERIC_AUTH0_PROVIDER = {
  label: 'Auth0',
  icon: <IconMdi path={mdiLockOutline} size={0.8} />,
};

const resolveProvider = (customProvider: string, email: string) => {
  const idpKey = customProvider.split('-')[0];

  if (idpKey === 'auth0' && !email.toLowerCase().endsWith(`@${ARTIFICIAL_STAFF_DOMAIN}`)) {
    return GENERIC_AUTH0_PROVIDER;
  }

  return PROVIDERS[idpKey] ?? { label: idpKey, icon: null };
};

const SignInButton: FC<SignInButtonProps> = ({ customProvider, email, onClick }) => {
  const handleLogin = (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    signInWithRedirect({ provider: { custom: customProvider }, options: { loginHint: email } });
  };

  const provider = resolveProvider(customProvider, email);

  return (
    <Button type="button" className="h-10 w-full" onClick={handleLogin}>
      {provider.icon}
      Sign in with {provider.label}
    </Button>
  );
};

export default SignInButton;
